/**
 * DOM-free HUD logic: what pressing a key would do to the current queue
 * (key-tile hints), and the one-line numbers / danger hints for the spell
 * preview cards (DESIGN 6.5). Numbers are summed from the SpellSpec packets
 * here so the HUD never calls the shared rules stubs; the authoritative
 * push algorithm stays in shared/queue.ts.
 */

import { MAX_QUEUE, OPPOSITES, type BaseElement, type DamagePacket, type Element, type SpellSpec } from '@shared/index.js';

export type TileHint =
  | { kind: 'add' }
  | { kind: 'combine'; into: Element }
  | { kind: 'degrade' }
  | { kind: 'cancel'; removes: Element }
  | { kind: 'full' }
  | { kind: 'shock' };

/** Display-only mirror of the DESIGN 2.2 push order (steps 1-6) for key-tile hints. */
export function tileHint(queue: readonly Element[], x: BaseElement, wet: boolean): TileHint {
  if (x === 'lightning' && wet) return { kind: 'shock' };
  if (x === 'fire' && queue.includes('ice')) return { kind: 'degrade' };
  if (x === 'cold' && queue.includes('steam')) return { kind: 'degrade' };
  if (x === 'fire' && queue.includes('water')) return { kind: 'combine', into: 'steam' };
  if (x === 'cold' && queue.includes('water')) return { kind: 'combine', into: 'ice' };
  for (let i = queue.length - 1; i >= 0; i--) {
    const y = queue[i]!;
    if (OPPOSITES[x].includes(y)) return { kind: 'cancel', removes: y };
  }
  if (queue.length >= MAX_QUEUE) return { kind: 'full' };
  return { kind: 'add' };
}

/** Sum of the damage numbers of a packet (life excluded: it heals the living). */
export function packetSum(p: DamagePacket): number {
  return p.physical + p.arcane + p.fire + p.cold + p.lightning + p.steam + p.water;
}

/** One-line numbers for a preview card ("283/tick x 11", "r 4.5u", "+179"). */
export function specNumbers(spec: SpellSpec): string {
  switch (spec.kind) {
    case 'beam': {
      const d = packetSum(spec.perTick);
      const parts: string[] = [];
      if (d > 0) parts.push(`${Math.round(d)}/tick`);
      if (spec.perTick.life > 0) parts.push(`heals ${Math.round(spec.perTick.life)}/tick`);
      parts.push(`x ${spec.ticks}`);
      return parts.join(' ');
    }
    case 'spray': return `${Math.round(packetSum(spec.perTick))}/tick x ${spec.ticks}, r ${spec.range.toFixed(0)}u`;
    case 'arc': return `${Math.round(packetSum(spec.perTick))}/tick, chains ${spec.chain}`;
    case 'projectile':
      return spec.solid === 'ice'
        ? `${spec.shards} shards, r ${spec.range}u`
        : `${Math.round(spec.direct)} + ${Math.round(packetSum(spec.payload))} blast r ${spec.blastRadius.toFixed(1)}u`;
    case 'nova': return `${Math.round(packetSum(spec.packet))}${spec.packet.life > 0 ? ` / +${Math.round(spec.packet.life)}` : ''} r ${spec.radius.toFixed(1)}u`;
    case 'quake': return `knockdown r ${spec.radius}u`;
    case 'barrier': return `${spec.hp} hp, ${spec.geometry}`;
    case 'wall': return `${spec.solid} ${spec.hp} hp, ${spec.lifetime}s`;
    case 'mines': return `${spec.count} mines, ${Math.round(packetSum(spec.packet) + spec.packet.life)} each`;
    case 'storm': return `${Math.round(packetSum(spec.perTick))}/tick, ${spec.lifetime}s`;
    case 'self': {
      const dmg = packetSum(spec.packet);
      const parts: string[] = [];
      if (spec.heal > 0) parts.push(`+${Math.round(spec.heal)}`);
      if (dmg > 0) parts.push(`-${Math.round(dmg)}`);
      if (spec.knockdown) parts.push('knockdown');
      if (spec.ward) parts.push(spec.ward.hp <= 1 ? `survive one killing blow, ${spec.ward.t}s` : `a killing blow leaves ${spec.ward.hp}, ${spec.ward.t}s`);
      if (spec.cleanse) parts.push(spec.cleanse === 'all' ? 'cleanses everything' : `washes off ${spec.cleanse.join(', ')}`);
      return parts.length > 0 ? parts.join(' ') : 'status on self';
    }
    case 'bubble': return `${spec.hp} hp bubble`;
    case 'aura': return `immune ${spec.immune.join(', ')} ${spec.lifetime}s`;
    case 'armor': return `${spec.solid} armor, absorbs ${spec.absorb}`;
    case 'melee': return `${Math.round(packetSum(spec.packet))} ${spec.shape}${spec.knockdown ? ', knockdown' : ''}`;
    case 'wind': return `push, ${spec.geometry}`;
    case 'field':
      if (spec.speed !== undefined) return `rolls ${Math.round(spec.speed * spec.lifetime)}u${spec.hit && packetSum(spec.hit) > 0 ? `, ${Math.round(packetSum(spec.hit))} each` : ''}`;
      return `r ${spec.radius}u, ${spec.lifetime}s${spec.scale < 1 ? `, time x${spec.scale}` : ''}`;
    case 'burst': return `${Math.round(packetSum(spec.packet))} r ${spec.radius}u, ${spec.dist}u ahead`;
    case 'mark': return `-${Math.round(spec.frac * 100)}% in ${spec.delay}s`;
    case 'blink': return `blink ${spec.dist}u`;
    case 'none': return '-';
  }
}

/** Red danger hint under a preview card; friendly fire is content, but say so (DESIGN pillar 3). */
export function specDanger(spec: SpellSpec, hp: number): string {
  switch (spec.kind) {
    case 'nova': case 'arc': case 'mines': case 'storm': case 'quake': case 'burst': return 'hits allies';
    case 'mark': return spec.reach === 'self' ? 'marks you' : '';
    case 'projectile': return spec.solid === 'earth' && packetSum(spec.payload) > 0 ? 'blast hits allies' : '';
    case 'self': {
      const d = packetSum(spec.packet);
      return d >= hp ? 'kills you' : d > 0 ? 'self-damage' : '';
    }
    default: return '';
  }
}
